/**
 * services/team.js — per-project Team roster (Reference > Team).
 *
 * Backed by the existing (previously unused-by-the-app) Airtable `Project Teams` table
 * (tbldMRG8xGcSttwj5) — one row per (Project, Contact). Grouping into Alder James / Client /
 * Subconsultants is DERIVED at read time, not stored:
 *   - Alder James  — the row's Contact belongs to the Organization named "Alder James"
 *   - Client       — the row's Contact belongs to the project's own Client organization
 *                     (Projects.Client, a single Organization link)
 *   - Subconsultant — the row's Contact belongs to an organization with an AWARDED
 *                     `Project Consultants` row on this project (see services/projectConsultants.js)
 *                     — NOT just "any other org," so a subconsultant branch's existence never depends
 *                     on a person having been added yet. Labeled "{Consultant Service} - {Org Name}".
 *
 * No Airtable schema changes for Project Teams itself: it has no Record-ID lookup companion for
 * `Project`, so rows are fetched broad and filtered client-side by checking the real linked ID in
 * `fields.Project` (multipleRecordLinks return real recXXX ids reliably — see CLAUDE.md's Airtable
 * API rules).
 */

import { apiGetAll, apiGetOne, apiPost, apiDelete } from './_api.js'
import { getOrganizations } from './contacts.js'
import { getCurrentTenant } from './tenancy.js'

const TBL_PROJ = 'tblgWX330MPJkhQVq'
const TBL_TEAM = 'tbldMRG8xGcSttwj5' // Project Teams

// The firm's own Organization record. Prefers the current tenant's configured
// home_organization_airtable_id (database/097_tenant_home_org.sql) — an explicit reference, so a
// future tenant just needs that one field set, no name-matching required. Falls back to a
// name-prefix heuristic (its real Organization Name is "Alder James Architecture", not the bare
// firm name) only if the tenant field isn't populated yet — defensive, not the primary path.
const ALDER_JAMES_PREFIX = 'alder james'
export function isAlderJamesOrg(org) {
  if (!org) return false
  const tenant = getCurrentTenant()
  if (tenant && tenant.homeOrganizationAirtableId) return org.id === tenant.homeOrganizationAirtableId
  return (org.fields['Organization Name'] || '').trim().toLowerCase().startsWith(ALDER_JAMES_PREFIX)
}

function getFirst(v) { return Array.isArray(v) ? (v[0] || null) : (v || null) }

/** The project's Client organization id (Projects.Client, single link), or null. */
export function getProjectClientOrgId(projectId) {
  return apiGetOne(TBL_PROJ, projectId).then(r => getFirst(r && r.fields && r.fields['Client']))
}

/** Every Project Teams row for this project. Full-fields fetch — lookups don't survive fields[]. */
export function getProjectTeamRows(projectId) {
  return apiGetAll(TBL_TEAM, '').then(rows => (rows || []).filter(r => {
    const proj = r.fields && r.fields['Project']
    return Array.isArray(proj) && proj.includes(projectId)
  }))
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim) instead
   of throwing — surface it so a bad write shows up as a real error, not a silent no-op. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

export async function addTeamMember(projectId, contactId) {
  return throwOnError(await apiPost(TBL_TEAM, { Project: [projectId], Contact: [contactId] }), 'team member')
}

export async function removeTeamMember(rowId) {
  return throwOnError(await apiDelete(TBL_TEAM, rowId), 'team member')
}

export { getOrganizations }

/**
 * The project's "current" General Contractor — the awarded construction contract with a linked
 * Organization (contractor_airtable_id), preferring one with status 'active' and otherwise the
 * most recently created. Most projects have exactly one live contract, so unlike Subconsultants
 * this is a single derived group (mirrors Client), not a candidate/award list — a GC's Team
 * presence follows directly from having a contract with the org linked, nothing else to manage
 * here. Returns null if no contract has an org link yet (see AddContractModal.jsx's "Linked
 * Organization" field, or Award, which sets it automatically from the winning bid).
 */
function resolveGcContract(contracts) {
  const withOrg = (contracts || []).filter(c => c.contractor_airtable_id)
  if (!withOrg.length) return null
  const active = withOrg.filter(c => c.status === 'active')
  const pool = active.length ? active : withOrg
  return pool.slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0]
}

/**
 * Bucket a project's team rows into { alderJames, client, generalContractor, subconsultants,
 * candidates } given the full Organizations list, the project's client org id, its Project
 * Consultants rows, and its construction contracts. subconsultants is an array of
 * { projectConsultantId, org, label, members } — one per AWARDED Project Consultants row, sorted
 * by label, always present even with zero members yet. candidates is the parallel list of
 * Candidate-status rows (no members — people aren't assignable until awarded). generalContractor
 * is { contractId, org, label, members } | null (see resolveGcContract). Team rows whose Contact's
 * org isn't Alder James, the client, the GC, or an awarded subconsultant are dropped (nothing to
 * bucket them under — e.g. the org's award was since revoked).
 */
export function groupTeamRows(teamRows, organizations, clientOrgId, projectConsultants, contracts) {
  const orgById = {}
  ;(organizations || []).forEach(org => { orgById[org.id] = org })
  const ajOrg = (organizations || []).find(isAlderJamesOrg)

  const subconsultants = []
  const candidates = []
  const membersByOrgId = {} // orgId -> members[], filled in below from teamRows

  ;(projectConsultants || []).forEach(pc => {
    const org = orgById[pc.orgId]
    if (!org) return // org deleted/inaccessible — nothing sane to show
    const label = (pc.consultantService || 'Consultant') + ' - ' + (org.fields['Organization Name'] || 'Unknown')
    if (pc.status === 'Awarded') {
      membersByOrgId[org.id] = membersByOrgId[org.id] || []
      subconsultants.push({ projectConsultantId: pc.id, org, label, members: membersByOrgId[org.id] })
    } else {
      candidates.push({ projectConsultantId: pc.id, org, label, consultantService: pc.consultantService })
    }
  })
  subconsultants.sort((a, b) => a.label.localeCompare(b.label))
  candidates.sort((a, b) => a.label.localeCompare(b.label))

  const gcContract = resolveGcContract(contracts)
  const gcOrg = gcContract ? orgById[gcContract.contractor_airtable_id] : null
  const generalContractor = gcOrg
    ? { contractId: gcContract.id, org: gcOrg, label: gcOrg.fields['Organization Name'] || 'General Contractor', members: [] }
    : null

  const alderJames = []
  const client = []

  ;(teamRows || []).forEach(row => {
    const contactId = getFirst(row.fields['Contact'])
    if (!contactId) return
    // Resolve the contact's org by scanning Organizations.Contacts (real ids) — same reverse-lookup
    // used before, just no longer used to invent a subconsultant bucket on the fly.
    const org = (organizations || []).find(o => ((o.fields && o.fields['Contacts']) || []).includes(contactId))
    if (!org) return
    const name = getFirst(row.fields['Name (from Contacts)']) || 'Unknown'
    const member = { rowId: row.id, contactId, name, orgId: org.id }

    if (ajOrg && org.id === ajOrg.id) { alderJames.push(member); return }
    if (clientOrgId && org.id === clientOrgId) { client.push(member); return }
    if (generalContractor && org.id === generalContractor.org.id) { generalContractor.members.push(member); return }
    if (membersByOrgId[org.id]) membersByOrgId[org.id].push(member) // only populated for awarded orgs
  })

  return { alderJames, client, generalContractor, subconsultants, candidates }
}
